import { useState, useEffect } from "react"
import { authFetch } from "../../utils/api"
import BuscadorMedico from "./BuscadorMedico"
import BuscadorProducto from "./BuscadorProducto"

const API = process.env.REACT_APP_API_URL
const POR_PAGINA = 8

const ESTADO_LABEL = { ACTIVO: "activo", INACTIVO: "inactivo", BAJA: "baja" }

const FORM_ALTA_VACIO = {
  obraSocialId: "", apellido: "", nombre: "", dni: "", nroSocio: "",
  nroCelular: "", domicilio: "", localidad: "", provincia: "",
  doctorId: "", enfAsignado: "", observaciones: "", estado: "ACTIVO",
}

function EditarPaciente() {
  // datos compartidos
  const [obrasSociales, setObrasSociales] = useState([])
  const [doctores, setDoctores]           = useState([])
  const [catalogo, setCatalogo]           = useState([])
  const [pacientes, setPacientes]         = useState([])
  const [cargando, setCargando]           = useState(true)

  // filtros / paginación
  const [busqueda, setBusqueda]           = useState("")
  const [mostrarBaja, setMostrarBaja]     = useState(false)
  const [pagina, setPagina]               = useState(1)

  // edición inline
  const [editandoId, setEditandoId]       = useState(null)
  const [formEditar, setFormEditar]       = useState(null)
  const [productosEditar, setProductosEditar] = useState([])
  const [prodSelEditar, setProdSelEditar] = useState("")
  const [prodCantEditar, setProdCantEditar] = useState(1)
  const [mensajeEditar, setMensajeEditar] = useState(null)

  // alta de paciente
  const [altaAbierta, setAltaAbierta]     = useState(false)
  const [formAlta, setFormAlta]           = useState(FORM_ALTA_VACIO)
  const [productosAlta, setProductosAlta] = useState([])
  const [prodSelAlta, setProdSelAlta]     = useState("")
  const [prodCantAlta, setProdCantAlta]   = useState(1)
  const [mensajeAlta, setMensajeAlta]     = useState(null)

  useEffect(() => {
    authFetch(`${API}/obras-sociales`).then(r => r.json()).then(setObrasSociales)
    authFetch(`${API}/doctores`).then(r => r.json()).then(setDoctores)
    authFetch(`${API}/productos`).then(r => r.json()).then(setCatalogo)
    authFetch(`${API}/pacientes`)
      .then(r => r.json())
      .then(setPacientes)
      .finally(() => setCargando(false))
  }, [])

  const refreshPacientes = () =>
    authFetch(`${API}/pacientes`).then(r => r.json()).then(setPacientes)

  // ── Alta ────────────────────────────────────────────────────────────────

  const handleToggleAlta = () => {
    setAltaAbierta(v => !v)
    setMensajeAlta(null)
  }

  const handleAgregarProductoAlta = () => {
    if (!prodSelAlta) return
    if (productosAlta.some(p => String(p.productoId) === prodSelAlta)) return
    setProductosAlta(prev => [...prev, { productoId: Number(prodSelAlta), cantidad: prodCantAlta }])
    setProdSelAlta(""); setProdCantAlta(1)
  }

  const handleSubmitAlta = async () => {
    setMensajeAlta(null)
    try {
      const res = await authFetch(`${API}/pacientes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formAlta),
      })
      if (!res.ok) {
        const err = await res.json()
        setMensajeAlta({ tipo: "error", texto: err.error || "Error al dar de alta" })
        return
      }
      const nuevo = await res.json()
      if (productosAlta.length > 0) {
        await authFetch(`${API}/pacientes/${nuevo.id}/productos`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productosAlta),
        })
      }
      setMensajeAlta({ tipo: "ok", texto: "Paciente dado de alta correctamente" })
      setFormAlta(FORM_ALTA_VACIO)
      setProductosAlta([])
      refreshPacientes()
    } catch {
      setMensajeAlta({ tipo: "error", texto: "Error de conexión" })
    }
  }

  // ── Edición ─────────────────────────────────────────────────────────────

  const handleEditar = async (p) => {
    if (editandoId === p.id) {
      setEditandoId(null); setFormEditar(null); setProductosEditar([]); setMensajeEditar(null)
      return
    }
    setMensajeEditar(null)
    const data = await authFetch(`${API}/pacientes/${p.id}`).then(r => r.json())
    setEditandoId(p.id)
    setFormEditar({
      obraSocialId:  data.obraSocialId  || "",
      apellido:      data.apellido      || "",
      nombre:        data.nombre        || "",
      dni:           data.dni           || "",
      nroSocio:      data.nroSocio      || "",
      nroCelular:    data.nroCelular    || "",
      domicilio:     data.domicilio     || "",
      localidad:     data.localidad     || "",
      provincia:     data.provincia     || "",
      doctorId:      data.doctor?.id    || "",
      enfAsignado:   data.enfAsignado   || "",
      observaciones: data.observaciones || "",
      estado:        data.estado        || "ACTIVO",
    })
    setProductosEditar((data.productos || []).map(pp => ({ productoId: pp.producto.id, cantidad: pp.cantidad })))
    setProdSelEditar(""); setProdCantEditar(1)
  }

  const handleAgregarProductoEditar = () => {
    if (!prodSelEditar) return
    if (productosEditar.some(p => String(p.productoId) === prodSelEditar)) return
    setProductosEditar(prev => [...prev, { productoId: Number(prodSelEditar), cantidad: prodCantEditar }])
    setProdSelEditar(""); setProdCantEditar(1)
  }

  const handleGuardar = async () => {
    if (!window.confirm("¿Confirmar los cambios en este paciente?")) return
    setMensajeEditar(null)
    try {
      const [res] = await Promise.all([
        authFetch(`${API}/pacientes/${editandoId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formEditar),
        }),
        authFetch(`${API}/pacientes/${editandoId}/productos`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(productosEditar),
        }),
      ])
      if (!res.ok) {
        const err = await res.json()
        setMensajeEditar({ tipo: "error", texto: err.error || "Error al guardar" })
        return
      }
      setMensajeEditar({ tipo: "ok", texto: "Paciente actualizado correctamente" })
      refreshPacientes()
    } catch {
      setMensajeEditar({ tipo: "error", texto: "Error de conexión" })
    }
  }

  // ── Filtros / paginación ─────────────────────────────────────────────────

  const q = busqueda.toLowerCase().trim()
  const pacientesFiltrados = pacientes.filter(p => {
    if (!mostrarBaja && p.pacienteEstado === "BAJA") return false
    return !q ||
      p.apellido?.toLowerCase().includes(q) ||
      p.nombre?.toLowerCase().includes(q) ||
      p.nroSocio?.toLowerCase().includes(q) ||
      p.dni?.toLowerCase().includes(q)
  })
  const totalPaginas    = Math.ceil(pacientesFiltrados.length / POR_PAGINA)
  const paginaActual    = Math.min(pagina, totalPaginas || 1)
  const pacientesPagina = pacientesFiltrados.slice((paginaActual - 1) * POR_PAGINA, paginaActual * POR_PAGINA)

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="adminPanel column">

      <div className="adminPanelTitle row">
        <i className="fa-solid fa-users"></i>
        GESTIÓN DE PACIENTES
      </div>

      {/* Botón toggle alta */}
      <div>
        <button
          className={`adminAltaToggleBtn${altaAbierta ? " adminAltaToggleBtn--active" : ""}`}
          onClick={handleToggleAlta}
        >
          <i className={`fa-solid fa-${altaAbierta ? "xmark" : "user-plus"}`}></i>
          {altaAbierta ? "cancelar" : "agregar paciente"}
        </button>
      </div>

      {/* Formulario de alta */}
      {altaAbierta && (
        <div className="adminAltaForm">
          <div className="adminSection">
            <div className="adminSectionLabel">datos del paciente</div>
            <div className="adminRow">
              <div className="adminField">
                <label>obra social</label>
                <select name="obraSocialId" value={formAlta.obraSocialId} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))}>
                  <option value="">seleccionar</option>
                  {obrasSociales.map(os => <option key={os.id} value={os.id}>{os.nombre}</option>)}
                </select>
              </div>
            </div>
            <div className="adminRow">
              {["apellido","nombre"].map(f => (
                <div key={f} className="adminField">
                  <label>{f}</label>
                  <input type="text" name={f} placeholder={f} value={formAlta[f]} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
                </div>
              ))}
              <div className="adminField">
                <label>n° dni</label>
                <input type="text" name="dni" placeholder="número" value={formAlta.dni} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
              <div className="adminField">
                <label>n° socio</label>
                <input type="text" name="nroSocio" placeholder="número de socio" value={formAlta.nroSocio} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
            </div>
            <div className="adminRow">
              <div className="adminField">
                <label>n° celular</label>
                <input type="text" name="nroCelular" placeholder="número" value={formAlta.nroCelular} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
              <div className="adminField">
                <label>enfermero asignado</label>
                <input type="text" name="enfAsignado" placeholder="nombre" value={formAlta.enfAsignado} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="adminSection">
            <div className="adminSectionLabel">domicilio</div>
            <div className="adminRow">
              <div className="adminField adminField--grow">
                <label>domicilio</label>
                <input type="text" name="domicilio" placeholder="calle y número" value={formAlta.domicilio} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
              <div className="adminField">
                <label>localidad</label>
                <input type="text" name="localidad" placeholder="localidad" value={formAlta.localidad} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
              <div className="adminField">
                <label>provincia</label>
                <input type="text" name="provincia" placeholder="provincia" value={formAlta.provincia} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="adminSection">
            <div className="adminSectionLabel">médico tratante</div>
            <div className="adminRow">
              <div className="adminField">
                <label>médico</label>
                <BuscadorMedico
                  doctores={doctores}
                  value={formAlta.doctorId}
                  onChange={v => setFormAlta(p => ({ ...p, doctorId: v }))}
                />
              </div>
            </div>
          </div>

          <div className="adminSection">
            <div className="adminSectionLabel">productos</div>
            <div className="adminRow">
              <div className="adminField">
                <label>producto</label>
                <BuscadorProducto
                  catalogo={catalogo.filter(c => !productosAlta.some(a => a.productoId === c.id))}
                  value={prodSelAlta}
                  onChange={setProdSelAlta}
                />
              </div>
              <div className="adminField adminField--narrow">
                <label>cantidad</label>
                <input type="number" min={1} value={prodCantAlta} onChange={e => setProdCantAlta(Number(e.target.value))} />
              </div>
              <button className="adminBtn adminBtn--primary adminBtnEnd" onClick={handleAgregarProductoAlta} disabled={!prodSelAlta}>
                + agregar
              </button>
            </div>
            {productosAlta.length > 0 && (
              <div className="adminProductosList">
                {productosAlta.map(pp => {
                  const prod = catalogo.find(c => c.id === pp.productoId)
                  return (
                    <div key={pp.productoId} className="adminProductoItemStatic">
                      <span className="productoMarca">{prod?.marca}</span>
                      <span className="productoDesc">{prod?.descripcion}</span>
                      <span className="productoCantidad">{pp.cantidad} caja/s por {prod?.unidad} unidades</span>
                      <button className="adminProductoQuitarBtn adminDanger" onClick={() => setProductosAlta(prev => prev.filter(p => p.productoId !== pp.productoId))} title="quitar">✕</button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="adminSection">
            <div className="adminSectionLabel">estado y observaciones</div>
            <div className="adminRow">
              <div className="adminField">
                <label>estado</label>
                <div className="adminRadioRow">
                  <label><input type="radio" name="estadoAlta" value="ACTIVO"   checked={formAlta.estado === "ACTIVO"}   onChange={e => setFormAlta(p => ({ ...p, estado: e.target.value }))} /> activo</label>
                  <label><input type="radio" name="estadoAlta" value="INACTIVO" checked={formAlta.estado === "INACTIVO"} onChange={e => setFormAlta(p => ({ ...p, estado: e.target.value }))} /> inactivo</label>
                  <label><input type="radio" name="estadoAlta" value="BAJA"     checked={formAlta.estado === "BAJA"}     onChange={e => setFormAlta(p => ({ ...p, estado: e.target.value }))} /> baja</label>
                </div>
              </div>
            </div>
            <div className="adminRowFull">
              <label>observaciones</label>
              <textarea name="observaciones" placeholder="observaciones" value={formAlta.observaciones} onChange={e => setFormAlta(p => ({ ...p, [e.target.name]: e.target.value }))} />
            </div>
          </div>

          {mensajeAlta && (
            <div className={`adminMensaje adminMensaje--${mensajeAlta.tipo}`}>
              {mensajeAlta.texto}
            </div>
          )}
          <div className="adminBtnRow adminBtnRow--mb">
            <button className="adminBtn adminBtn--primary" onClick={handleSubmitAlta}>dar de alta</button>
            <button className="adminBtn" onClick={handleToggleAlta}>cancelar</button>
          </div>
        </div>
      )}

      <div className="adminSeparador" />

      {/* Tabla de pacientes */}
      {cargando && <div className="adminEmptyState">cargando...</div>}

      {!cargando && (
        <div className="adminPreviewBox">
          <div className="adminBusquedaRow">
            <input
              type="text"
              placeholder="buscar por nombre, n° socio o DNI..."
              value={busqueda}
              onChange={e => { setBusqueda(e.target.value); setEditandoId(null); setPagina(1) }}
            />
            <label className="adminCheckboxLabel">
              <input type="checkbox" checked={mostrarBaja} onChange={e => { setMostrarBaja(e.target.checked); setPagina(1) }} />
              mostrar dados de baja
            </label>
            {busqueda && (
              <span className="adminBusquedaInfo">
                {pacientesFiltrados.length} de {pacientes.length} pacientes
              </span>
            )}
          </div>

          <table className="adminPreviewTable">
            <thead>
              <tr>
                <th></th>
                <th>apellido</th>
                <th>nombre</th>
                <th>obra social</th>
                <th>n° socio</th>
                <th>DNI</th>
                <th>estado</th>
              </tr>
            </thead>
            <tbody>
              {pacientesPagina.length === 0 ? (
                <tr><td colSpan={7}><div className="adminEmptyState">sin resultados</div></td></tr>
              ) : pacientesPagina.map(p => (
                <>
                  <tr key={p.id} className={editandoId === p.id ? "usuarioRowActiva" : ""}>
                    <td>
                      <button
                        className={`usuarioEditBtn${editandoId === p.id ? " usuarioEditBtn--active" : ""}`}
                        onClick={() => handleEditar(p)}
                        title="editar paciente"
                      >
                        <i className={`fa-solid fa-${editandoId === p.id ? "xmark" : "pen"}`}></i>
                      </button>
                    </td>
                    <td>{p.apellido}</td>
                    <td>{p.nombre}</td>
                    <td>{p.prestador || <span className="adminPlaceholder">—</span>}</td>
                    <td>{p.nroSocio  || <span className="adminPlaceholder">—</span>}</td>
                    <td>{p.dni       || <span className="adminPlaceholder">—</span>}</td>
                    <td>
                      <span className={`usuarioRolBadge pacienteEstadoBadge--${p.pacienteEstado?.toLowerCase()}`}>
                        {ESTADO_LABEL[p.pacienteEstado] ?? p.pacienteEstado}
                      </span>
                    </td>
                  </tr>

                  {editandoId === p.id && formEditar && (
                    <tr key={`${p.id}-form`} className="usuarioFormRow">
                      <td colSpan={7}>
                        <div className="usuarioFormInline">

                          <div className="adminSection">
                            <div className="adminSectionLabel">datos del paciente</div>
                            <div className="adminRow">
                              <div className="adminField">
                                <label>apellido</label>
                                <input type="text" name="apellido" value={formEditar.apellido} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                              <div className="adminField">
                                <label>nombre</label>
                                <input type="text" name="nombre" value={formEditar.nombre} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                              <div className="adminField">
                                <label>n° dni</label>
                                <input type="text" name="dni" value={formEditar.dni} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                              <div className="adminField">
                                <label>n° socio</label>
                                <input type="text" name="nroSocio" value={formEditar.nroSocio} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                            </div>
                            <div className="adminRow">
                              <div className="adminField">
                                <label>n° celular</label>
                                <input type="text" name="nroCelular" value={formEditar.nroCelular} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                              <div className="adminField">
                                <label>enfermero asignado</label>
                                <input type="text" name="enfAsignado" value={formEditar.enfAsignado} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                            </div>
                          </div>

                          <div className="adminSection">
                            <div className="adminSectionLabel">domicilio</div>
                            <div className="adminRow">
                              <div className="adminField adminField--grow">
                                <label>domicilio</label>
                                <input type="text" name="domicilio" value={formEditar.domicilio} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                              <div className="adminField">
                                <label>localidad</label>
                                <input type="text" name="localidad" value={formEditar.localidad} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                              <div className="adminField">
                                <label>provincia</label>
                                <input type="text" name="provincia" value={formEditar.provincia} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                              </div>
                            </div>
                          </div>

                          <div className="adminSection">
                            <div className="adminSectionLabel">médico tratante</div>
                            <div className="adminRow">
                              <div className="adminField">
                                <label>médico</label>
                                <BuscadorMedico
                                  doctores={doctores}
                                  value={formEditar.doctorId}
                                  onChange={v => setFormEditar(f => ({ ...f, doctorId: v }))}
                                />
                              </div>
                            </div>
                          </div>

                          <div className="adminSection">
                            <div className="adminSectionLabel">productos</div>
                            <div className="adminRow">
                              <div className="adminField">
                                <label>producto</label>
                                <BuscadorProducto
                                  catalogo={catalogo.filter(c => !productosEditar.some(a => a.productoId === c.id))}
                                  value={prodSelEditar}
                                  onChange={setProdSelEditar}
                                />
                              </div>
                              <div className="adminField adminField--narrow">
                                <label>cantidad</label>
                                <input type="number" min={1} value={prodCantEditar} onChange={e => setProdCantEditar(Number(e.target.value))} />
                              </div>
                              <button className="adminBtn adminBtn--primary adminBtnEnd" onClick={handleAgregarProductoEditar} disabled={!prodSelEditar}>
                                + agregar
                              </button>
                            </div>
                            {productosEditar.length > 0 && (
                              <div className="adminProductosList">
                                {productosEditar.map(pp => {
                                  const prod = catalogo.find(c => c.id === pp.productoId)
                                  return (
                                    <div key={pp.productoId} className="adminProductoItemStatic">
                                      <span className="productoMarca">{prod?.marca}</span>
                                      <span className="productoDesc">{prod?.descripcion}</span>
                                      <span className="productoCantidad">{pp.cantidad} caja/s por {prod?.unidad} unidades</span>
                                      <button className="adminProductoQuitarBtn adminDanger" onClick={() => setProductosEditar(prev => prev.filter(p => p.productoId !== pp.productoId))} title="quitar">✕</button>
                                    </div>
                                  )
                                })}
                              </div>
                            )}
                          </div>

                          <div className="adminSection">
                            <div className="adminSectionLabel">estado y observaciones</div>
                            <div className="adminRow">
                              <div className="adminField">
                                <label>estado</label>
                                <div className="adminRadioRow">
                                  <label><input type="radio" name="estadoEditar" value="ACTIVO"   checked={formEditar.estado === "ACTIVO"}   onChange={e => setFormEditar(f => ({ ...f, estado: e.target.value }))} /> activo</label>
                                  <label><input type="radio" name="estadoEditar" value="INACTIVO" checked={formEditar.estado === "INACTIVO"} onChange={e => setFormEditar(f => ({ ...f, estado: e.target.value }))} /> inactivo</label>
                                  <label><input type="radio" name="estadoEditar" value="BAJA"     checked={formEditar.estado === "BAJA"}     onChange={e => setFormEditar(f => ({ ...f, estado: e.target.value }))} /> baja</label>
                                </div>
                              </div>
                            </div>
                            <div className="adminRowFull">
                              <label>observaciones</label>
                              <textarea name="observaciones" value={formEditar.observaciones} onChange={e => setFormEditar(f => ({ ...f, [e.target.name]: e.target.value }))} />
                            </div>
                          </div>

                          {mensajeEditar && (
                            <div className={`adminMensaje adminMensaje--${mensajeEditar.tipo}`}>
                              {mensajeEditar.texto}
                            </div>
                          )}
                          <div className="adminBtnRow">
                            <button className="adminBtn adminBtn--primary" onClick={handleGuardar}>guardar cambios</button>
                            <button className="adminBtn" onClick={() => { setEditandoId(null); setFormEditar(null); setMensajeEditar(null) }}>cancelar</button>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!cargando && totalPaginas > 1 && (
        <div className="adminPaginacion">
          <button className="adminBtn" onClick={() => { setPagina(p => p - 1); setEditandoId(null) }} disabled={paginaActual === 1}>{"<"}</button>
          <span className="adminPaginacionInfo">{paginaActual} / {totalPaginas}</span>
          <button className="adminBtn" onClick={() => { setPagina(p => p + 1); setEditandoId(null) }} disabled={paginaActual === totalPaginas}>{">"}</button>
        </div>
      )}

    </div>
  )
}

export default EditarPaciente
